import React from 'react';
import PropTypes from 'prop-types';
import {m} from 'framer-motion';
// @mui
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
// @mui/icons-material
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
// theme
import {ADMIN_PALETTE} from 'packages/common/theme/admin';
// auth
import {useAuthContext} from 'packages/common/hooks';
import {useSession} from 'packages/common/hooks/session';
// utils
import {getRoleLabel} from 'packages/common/utils/rolesConfig';
// components
import CompanySwitcher from 'packages/common/components/company-switcher/CompanySwitcher';
import CustomPopover, {usePopover} from 'packages/common/components/custom-popover';

/**
 * Section compte utilisateur dans le footer de la navigation verticale
 *
 * Affiche l'avatar et le nom de l'utilisateur connecté avec un menu
 * contextuel permettant la déconnexion. Utilise un style Neumorphism
 * avec effet de relief 3D pour un rendu moderne et élégant.
 *
 * @module NavAccountSection
 * @author Équipe Brydge
 * @since 2.0.0
 */

/**
 * Composant de section compte pour la barre de navigation
 *
 * @param {boolean} isMini - Mode compacté (affiche uniquement l'avatar)
 * @return {JSX.Element} Section avec avatar et menu utilisateur
 */
export default function NavAccountSection({isMini = false}) {
  const {logout} = useAuthContext();
  const popover = usePopover();
  const {user} = useSession();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error);
    }
  };

  // Version mini : juste l'avatar centré
  if (isMini) {
    return (
      <>
        <Divider
          sx={{
            borderStyle: 'dashed',
            mb: 1.5,
            opacity: 0.15,
            borderColor: ADMIN_PALETTE.nav.divider,
          }}
        />

        {/* Company Switcher en mini */}
        <Stack spacing={1} alignItems="center" sx={{mb: 1.5, px: 1}}>
          <CompanySwitcher variant="icon" size="small" />
        </Stack>

        <IconButton
          onClick={popover.onOpen}
          sx={{
            'mx': 'auto',
            'mb': 2,
            'p': 0.5,
            'border': `2px solid ${ADMIN_PALETTE.accent.bg30}`,
            'bgcolor': ADMIN_PALETTE.interactive.hoverSoft,
            '&:hover': {
              bgcolor: ADMIN_PALETTE.accent.bg15,
              borderColor: ADMIN_PALETTE.accent.bg50,
            },
          }}
        >
          <Avatar
            src={user?.photoURL}
            alt={`${user?.firstName} ${user?.lastName}`}
            sx={{
              width: 36,
              height: 36,
            }}
          >
            {user?.firstName?.charAt(0)?.toUpperCase() || ''}
          </Avatar>
        </IconButton>

        <CustomPopover
          hiddenArrow
          open={popover.open}
          onClose={popover.onClose}
          sx={{
            width: 280,
            p: 0,
            bgcolor: (theme) => theme.palette.navy[900],
            backgroundImage: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(15, 28, 46, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'bottom', horizontal: 'left'}}
        >
          <Box sx={{p: 2.5}}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                src={user?.photoURL}
                alt={`${user?.firstName} ${user?.lastName}`}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: (theme) => theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {user?.firstName?.charAt(0)?.toUpperCase() || ''}
              </Avatar>
              <Stack sx={{minWidth: 0, flex: 1}}>
                <Typography variant="subtitle2" noWrap sx={{color: '#E2E8F0'}}>
                  {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{color: 'rgba(226, 232, 240, 0.7)', fontSize: '0.8rem'}}
                  noWrap
                >
                  {user?.email}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />

          <Box sx={{p: 1}}>
            <MenuItem
              onClick={handleLogout}
              sx={{
                'px': 2,
                'py': 1.5,
                'gap': 1.5,
                'borderRadius': 1,
                'color': 'error.main',
                'transition': 'all 0.2s',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                },
              }}
            >
              <ListItemIcon sx={{minWidth: 'auto'}}>
                <LogoutRoundedIcon sx={{fontSize: 20, color: 'error.main'}} />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={600}>
                Déconnexion
              </Typography>
            </MenuItem>
          </Box>
        </CustomPopover>
      </>
    );
  }

  // Version normale : carte complète avec nom et rôle
  return (
    <>
      <Divider
        sx={{borderStyle: 'dashed', mb: 2.5, opacity: 0.15, borderColor: ADMIN_PALETTE.nav.divider}}
      />

      {/* Company Switcher */}
      <Box sx={{px: 2, mb: 1.5}}>
        <CompanySwitcher variant="compact" size="medium" />
      </Box>

      <Box
        component={m.div}
        whileHover={{y: -2}}
        whileTap={{y: 0}}
        onClick={popover.onOpen}
        sx={{
          'mx': 1.5,
          'mb': 2,
          'cursor': 'pointer',
          'borderRadius': 2,
          'transition': 'all 0.2s',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <Stack sx={{p: 1.5}} spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              src={user?.photoURL}
              alt={`${user?.firstName} ${user?.lastName}`}
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                fontSize: '0.75rem',
                bgcolor: (theme) => theme.palette.primary.main,
                color: 'white',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || ''}
            </Avatar>

            <Stack sx={{minWidth: 0, flex: 1}} spacing={0.3}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{lineHeight: 1.3, color: ADMIN_PALETTE.text.primary}}
              >
                {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              >
                {user?.role ? getRoleLabel(user.role) : 'Utilisateur'}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <CustomPopover
        hiddenArrow
        open={popover.open}
        onClose={popover.onClose}
        sx={{
          width: 280,
          p: 0,
          bgcolor: (theme) => theme.palette.navy[900],
          backgroundImage: 'none',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(15, 28, 46, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        anchorOrigin={{vertical: 'top', horizontal: 'left'}}
        transformOrigin={{vertical: 'bottom', horizontal: 'left'}}
      >
        <Box sx={{p: 2.5}}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={user?.photoURL}
              alt={`${user?.firstName} ${user?.lastName}`}
              sx={{
                width: 40,
                height: 40,
                bgcolor: (theme) => theme.palette.primary.main,
                color: 'white',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || ''}
            </Avatar>
            <Stack sx={{minWidth: 0, flex: 1}}>
              <Typography variant="subtitle2" noWrap sx={{color: '#E2E8F0'}}>
                {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
              </Typography>
              <Typography
                variant="body2"
                sx={{color: 'rgba(226, 232, 240, 0.7)', fontSize: '0.8rem'}}
                noWrap
              >
                {user?.email}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />

        <Box sx={{p: 1}}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              'px': 2,
              'py': 1.5,
              'gap': 1.5,
              'borderRadius': 1,
              'color': 'error.main',
              'transition': 'all 0.2s',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
              },
            }}
          >
            <ListItemIcon sx={{minWidth: 'auto'}}>
              <LogoutRoundedIcon sx={{fontSize: 20, color: 'error.main'}} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={600}>
              Déconnexion
            </Typography>
          </MenuItem>
        </Box>
      </CustomPopover>
    </>
  );
}

NavAccountSection.propTypes = {
  isMini: PropTypes.bool,
};
